import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";
import { throttle } from "lodash";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Button } from "primereact/button";
import { Chip } from "primereact/chip";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Menu } from "primereact/menu";
import { Paginator } from "primereact/paginator";
import { ProgressSpinner } from "primereact/progressspinner";
import { SelectButton } from "primereact/selectbutton";
import { Skeleton } from "primereact/skeleton";
import PageHeading from "../../../components/PageHeading";
import PageLoadError from "../../../components/PageLoadError";
import { useAppContext } from "../../../context/appContext";
import authApi from "../../../service/IdApi";
import JobStockCropDialog from "./JobStockCropDialog";

const VIEWPORT_OPTIONS = [
    { label: "Mobile", value: "mobile", width: 600, icon: "pi pi-mobile" },
    { label: "Tablet", value: "tablet", width: 1280, icon: "pi pi-tablet" },
    { label: "Desktop", value: "desktop", width: 1920, icon: "pi pi-desktop" },
];

const SORT_OPTIONS = [
    { label: "Date (Newest First)", value: "createdAt_desc", sortField: "createdAt", sortOrder: -1 },
    { label: "Date (Oldest First)", value: "createdAt_asc", sortField: "createdAt", sortOrder: 1 },
    { label: "Popularity (Most Used)", value: "usageCount_desc", sortField: "usageCount", sortOrder: -1 },
    { label: "Popularity (Least Used)", value: "usageCount_asc", sortField: "usageCount", sortOrder: 1 },
];

const DEFAULT_SORT = SORT_OPTIONS[0];

/** Matches JobStockService.CURRENT_VERSION — location-specific geographic fit. */
const CURRENT_VERSION = 6;

const AI_IMAGE_PROVIDER_RECRAFT = "recraft";
const AI_IMAGE_PROVIDER_BLACK_FOREST_LABS = "blackForestLabs";
const AI_IMAGE_PROVIDER_BOTH = "both";

const AI_IMAGE_PROVIDER_OPTIONS = [
    { label: "Recraft", value: AI_IMAGE_PROVIDER_RECRAFT },
    { label: "Black Forest Labs (Flux 2 Pro)", value: AI_IMAGE_PROVIDER_BLACK_FOREST_LABS },
];

const GENERATE_AI_IMAGE_PROVIDER_OPTIONS = [
    ...AI_IMAGE_PROVIDER_OPTIONS,
    { label: "Both", value: AI_IMAGE_PROVIDER_BOTH },
];

const GENERATE_KIND_HERO = "hero";
const GENERATE_KIND_OFFER_ICON = "offerIcon";

const GENERATE_KIND_OPTIONS = [
    { label: "Hero Image", value: GENERATE_KIND_HERO },
    { label: "Offer Icon", value: GENERATE_KIND_OFFER_ICON },
];

/** Mirrors JobStockAdminGeneratePrompts.defaultHeroPrompt() */
const HERO_SCENE_DEFAULT =
    "Create a professional website hero banner photograph. Depict a concrete real-world scene of the work or its result - not marketing copy, graphics, or collage. Use a bold, welcoming composition suitable for a home page hero";
const HERO_PROMPT_SUFFIX =
    " Photorealistic photography style. Do not include any text, letters, numbers, words, logos, watermarks, captions, signs, labels, or typography in the image.\n\nDo not include any people in the image.";
const DEFAULT_HERO_PROMPT = `${HERO_SCENE_DEFAULT}.${HERO_PROMPT_SUFFIX}`;

const formatAiImageProvider = (provider) => {
    if (provider === AI_IMAGE_PROVIDER_BLACK_FOREST_LABS) {
        return "Black Forest Labs (Flux 2 Pro)";
    }
    if (provider === AI_IMAGE_PROVIDER_RECRAFT) {
        return "Recraft";
    }
    return provider || null;
};

const resolveAiImageProvider = (item) => {
    if (item?.aiImageProvider === AI_IMAGE_PROVIDER_BLACK_FOREST_LABS) {
        return AI_IMAGE_PROVIDER_BLACK_FOREST_LABS;
    }
    return AI_IMAGE_PROVIDER_RECRAFT;
};

const isDevelopmentEnvironment = process.env.REACT_APP_ENVIRONMENT === "development";

const PEOPLE_FILTER_OPTIONS = [
    { label: "All people", value: "" },
    { label: "With people", value: "yes" },
    { label: "Without people", value: "no" },
];

const TEXT_FILTER_OPTIONS = [
    { label: "All text", value: "" },
    { label: "Contains text", value: "yes" },
    { label: "No text", value: "no" },
];

const PROPERTY_TYPE_FILTER_OPTIONS = [
    { label: "All property types", value: "" },
    { label: "Residential", value: "residential" },
    { label: "Commercial", value: "commercial" },
];

const IDEAL_PAGE_TYPE_CONTACT = "contact";
const IDEAL_PAGE_TYPE_REVIEWS = "reviews";
const IDEAL_PAGE_TYPE_CLIENT_HUB = "client_hub";

const IDEAL_PAGE_TYPE_FILTER_OPTIONS = [
    { label: "All page types", value: "" },
    { label: "Contact Us", value: IDEAL_PAGE_TYPE_CONTACT },
    { label: "Reviews", value: IDEAL_PAGE_TYPE_REVIEWS },
    { label: "Client Hub", value: IDEAL_PAGE_TYPE_CLIENT_HUB },
];

const IDEAL_PAGE_TYPE_EDIT_OPTIONS = [
    { label: "None", value: "" },
    { label: "Contact Us", value: IDEAL_PAGE_TYPE_CONTACT },
    { label: "Reviews", value: IDEAL_PAGE_TYPE_REVIEWS },
    { label: "Client Hub", value: IDEAL_PAGE_TYPE_CLIENT_HUB },
];

const ORIENTATION_FILTER_OPTIONS = [
    { label: "All orientations", value: "" },
    { label: "Portrait", value: "portrait" },
    { label: "Landscape", value: "landscape" },
    { label: "Square", value: "square" },
    { label: "Panoramic horizontal", value: "panoramic_horizontal" },
    { label: "Panoramic vertical", value: "panoramic_vertical" },
];

const VERSION_FILTER_OPTIONS = (() => {
    const options = [
        { label: "All versions", value: "" },
        { label: `Needs re-analyze (< v${CURRENT_VERSION})`, value: "outdated" },
    ];
    for (let version = 2; version <= CURRENT_VERSION; version++) {
        const suffix = version === CURRENT_VERSION ? " (current)" : "";
        options.push({
            label: `Version ${version}${suffix}`,
            value: String(version),
        });
    }
    return options;
})();

const REVIEWED_FILTER_OPTIONS = [
    { label: "All", value: "" },
    { label: "Not reviewed", value: "no" },
    { label: "Reviewed", value: "yes" },
];

const ARCHIVED_FILTER_OPTIONS = [
    { label: "All", value: "" },
    { label: "Active", value: "no" },
    { label: "Archived", value: "yes" },
];

const ETHNICITY_FILTER_OPTIONS = [
    { label: "All ethnicities", value: "" },
    { label: "Black", value: "Black" },
    { label: "White", value: "White" },
    { label: "East Asian", value: "East Asian" },
    { label: "South Asian", value: "South Asian" },
    { label: "Hispanic", value: "Hispanic" },
    { label: "Middle Eastern", value: "Middle Eastern" },
    { label: "Multiracial Person", value: "Multiracial Person" },
    { label: "Multiracial Group", value: "Multiracial Group" },
    { label: "Native American", value: "Native American" },
    { label: "Pacific Islander", value: "Pacific Islander" },
    { label: "Southeast Asian", value: "Southeast Asian" },
];

const MEDIA_TYPE_FILTER_OPTIONS = [
    { label: "All types", value: "" },
    { label: "Images", value: "image" },
    { label: "Videos", value: "video" },
];

/** Filter dropdown fields (sort / refresh / upload stay always visible). */
const FILTER_FIELDS = [
    {
        key: "type",
        label: "Type",
        placeholder: "Type",
        options: MEDIA_TYPE_FILTER_OPTIONS,
    },
    {
        key: "hasPeople",
        label: "People",
        placeholder: "People",
        options: PEOPLE_FILTER_OPTIONS,
    },
    {
        key: "includesText",
        label: "Text",
        placeholder: "Text",
        options: TEXT_FILTER_OPTIONS,
    },
    {
        key: "ethnicity",
        label: "Ethnicity",
        placeholder: "Ethnicity",
        options: ETHNICITY_FILTER_OPTIONS,
    },
    {
        key: "propertyType",
        label: "Property type",
        placeholder: "Property type",
        options: PROPERTY_TYPE_FILTER_OPTIONS,
    },
    {
        key: "idealPageType",
        label: "Ideal page",
        placeholder: "Ideal page",
        options: IDEAL_PAGE_TYPE_FILTER_OPTIONS,
    },
    {
        key: "categoryUri",
        label: "Category",
        placeholder: "Category",
        catalog: "categories",
        filter: true,
        showClear: true,
    },
    {
        key: "subcategoryUri",
        label: "Subcategory",
        placeholder: "Subcategory",
        catalog: "subcategories",
        filter: true,
        showClear: true,
    },
    {
        key: "offerUri",
        label: "Offer",
        placeholder: "Offer",
        catalog: "offers",
        filter: true,
        showClear: true,
    },
    {
        key: "orientation",
        label: "Orientation",
        placeholder: "Orientation",
        options: ORIENTATION_FILTER_OPTIONS,
    },
    {
        key: "version",
        label: "Version",
        placeholder: "Version",
        options: VERSION_FILTER_OPTIONS,
    },
    {
        key: "reviewed",
        label: "Reviewed",
        placeholder: "Reviewed",
        options: REVIEWED_FILTER_OPTIONS,
    },
    {
        key: "archived",
        label: "Archived",
        placeholder: "Archived",
        options: ARCHIVED_FILTER_OPTIONS,
    },
];

const DEFAULT_VISIBLE_FILTER_KEYS = ["type", "reviewed", "archived"];

const resolveSortOption = (sortValue) => {
    for (let i = 0; i < SORT_OPTIONS.length; i++) {
        if (SORT_OPTIONS[i].value === sortValue) {
            return SORT_OPTIONS[i];
        }
    }
    return DEFAULT_SORT;
};

const buildCatalogFilterOptions = (entries, allLabel) => {
    const options = [{ label: allLabel, value: "" }];
    if (!Array.isArray(entries)) {
        return options;
    }
    for (let i = 0; i < entries.length; i++) {
        if (entries[i]?.uri && entries[i]?.name) {
            options.push({
                label: entries[i].name,
                value: entries[i].uri,
            });
        }
    }
    return options;
};

const findFilterField = (key) => {
    for (let i = 0; i < FILTER_FIELDS.length; i++) {
        if (FILTER_FIELDS[i].key === key) {
            return FILTER_FIELDS[i];
        }
    }
    return null;
};

const formatEthnicities = (item) => {
    const ethnicities = item?.vision?.ethnicities;
    if (!ethnicities || ethnicities.length === 0) {
        return null;
    }
    return ethnicities.join(", ");
};

const formatLocationSpecific = (locationSpecific) => {
    if (locationSpecific === true) {
        return "Yes";
    }
    if (locationSpecific === false) {
        return "No";
    }
    return null;
};

const formatStateCodes = (stateCodes) => {
    if (!stateCodes || stateCodes.length === 0) {
        return null;
    }
    return stateCodes.join(", ");
};

const getLatestVisionAnalysisError = (item) => {
    const analyzers = item?.vision?.analyzers;
    if (!analyzers || analyzers.length === 0) {
        return null;
    }
    for (let i = analyzers.length - 1; i >= 0; i--) {
        const error = analyzers[i]?.analysisError;
        if (error) {
            return error;
        }
    }
    return null;
};

const isBusyItem = (item) => {
    return item?.status === "processing" || item?.aiGenerating === true;
};

const itemHasVisibleText = (item) => {
    return item?.vision?.includesText === "yes";
};

const getItemVersion = (item) => {
    if (item?.version == null) {
        return 2;
    }
    return item.version;
};

const needsReanalyze = (item) => {
    if (!item || item.type === "video") {
        return false;
    }
    if (item.status !== "ready") {
        return false;
    }
    return getItemVersion(item) < CURRENT_VERSION;
};

const hasUsedFor = (variant, role) => {
    if (!variant?.usedFor || !role) {
        return false;
    }
    for (let i = 0; i < variant.usedFor.length; i++) {
        if (variant.usedFor[i] === role) {
            return true;
        }
    }
    return false;
};

const getWidthVariants = (item) => {
    if (!item?.variants) {
        return [];
    }
    const widthVariants = [];
    for (let i = 0; i < item.variants.length; i++) {
        const variant = item.variants[i];
        if (!variant || !hasUsedFor(variant, "display") || !variant.width || !variant.publicUrl) {
            continue;
        }
        widthVariants.push(variant);
    }
    widthVariants.sort((a, b) => a.width - b.width);
    return widthVariants;
};

/**
 * JobStock AI regenerate overwrites the same S3 keys, so publicUrl strings stay
 * identical. Append a token derived from variant byte sizes / dimensions so
 * browser (and CDN) caches do not keep showing the previous image in admin previews.
 */
const appendCacheBust = (url, token) => {
    if (!url || token == null || token === "") {
        return url || undefined;
    }
    const separator = url.indexOf("?") >= 0 ? "&" : "?";
    return url + separator + "v=" + encodeURIComponent(String(token));
};

const getMediaCacheToken = (item, sessionRevision) => {
    if (!item) {
        return undefined;
    }
    const parts = [];
    if (item.width != null) {
        parts.push("w" + item.width);
    }
    if (item.height != null) {
        parts.push("h" + item.height);
    }
    if (item.variants) {
        for (let i = 0; i < item.variants.length; i++) {
            const variant = item.variants[i];
            if (variant?.size != null) {
                parts.push(String(variant.size));
            }
        }
    }
    const analyzers = item?.vision?.analyzers;
    if (analyzers && analyzers.length > 0) {
        const lastAnalyzer = analyzers[analyzers.length - 1];
        if (lastAnalyzer?.analyzedAt) {
            parts.push(String(lastAnalyzer.analyzedAt));
        }
    }
    if (sessionRevision != null && sessionRevision !== "") {
        parts.push("r" + sessionRevision);
    }
    if (parts.length === 0) {
        return undefined;
    }
    return parts.join("-");
};

const buildSrcSet = (item, sessionRevision) => {
    const widthVariants = getWidthVariants(item);
    if (widthVariants.length === 0) {
        return undefined;
    }
    const token = getMediaCacheToken(item, sessionRevision);
    return widthVariants
        .map((variant) => `${appendCacheBust(variant.publicUrl, token)} ${variant.width}w`)
        .join(", ");
};

const formatCatalogNames = (items) => {
    if (!items || items.length === 0) {
        return null;
    }
    const names = [];
    for (let i = 0; i < items.length; i++) {
        if (items[i]?.name) {
            names.push(items[i].name);
        }
    }
    if (names.length === 0) {
        return null;
    }
    return names.join(", ");
};

const formatCatalogChipLabel = (item) => {
    const name = item?.name || item?.uri || "";
    if (item?.confidence == null || Number.isNaN(Number(item.confidence))) {
        return name;
    }
    const percent = Math.round(Number(item.confidence) * 100);
    return `${name} · ${percent}%`;
};

const RemovableCatalogTags = ({
    label,
    kind,
    items,
    removingKey,
    onRemove,
    allowEmpty = false,
    footer = null,
}) => {
    const list = Array.isArray(items) ? items.slice() : [];
    list.sort((a, b) => {
        const aConfidence = a?.confidence == null || Number.isNaN(Number(a.confidence))
            ? -1
            : Number(a.confidence);
        const bConfidence = b?.confidence == null || Number.isNaN(Number(b.confidence))
            ? -1
            : Number(b.confidence);
        return bConfidence - aConfidence;
    });
    if (list.length === 0 && !allowEmpty) {
        return null;
    }
    return (
        <div className="mb-3">
            <style>{`
                .jobstock-vision-chip.p-chip {
                    font-size: 14px;
                    padding: 0.1rem 0.35rem;
                    gap: 0.25rem;
                }
                .jobstock-vision-chip.p-chip .p-chip-text {
                    font-size: 14px;
                    line-height: 1.25;
                    margin: 0;
                }
                .jobstock-vision-chip.p-chip .p-chip-remove-icon {
                    font-size: 0.75rem;
                    margin-left: 0;
                    margin-right: 0.15rem;
                    order: -1;
                }
            `}</style>
            <div className="text-xs font-semibold text-500 uppercase mb-1 flex align-items-center gap-2">
                <span>{label}</span>
                <span className="font-normal normal-case text-400">
                    ({list.length}){list.length > 0 ? " — click × to remove" : ""}
                </span>
            </div>
            {list.length > 0 && (
                <div
                    className="flex flex-column align-items-start gap-1 overflow-y-auto"
                    style={{ maxHeight: "11rem" }}
                >
                    {list.map((item) => {
                        if (!item?.uri) {
                            return null;
                        }
                        const key = `${kind}:${item.uri}`;
                        const isRemoving = removingKey === key;
                        return (
                            <Chip
                                key={key}
                                label={formatCatalogChipLabel(item)}
                                removable={!isRemoving}
                                onRemove={() => {
                                    onRemove(kind, item.uri);
                                }}
                                className={isRemoving ? "opacity-50 jobstock-vision-chip" : "jobstock-vision-chip"}
                            />
                        );
                    })}
                </div>
            )}
            {footer}
        </div>
    );
};

const formatPropertyType = (propertyType) => {
    if (propertyType === "residential") {
        return "Residential";
    }
    if (propertyType === "commercial") {
        return "Commercial";
    }
    return propertyType || null;
};

const formatIdealPageType = (idealPageType) => {
    if (idealPageType === IDEAL_PAGE_TYPE_CONTACT) {
        return "Contact Us";
    }
    if (idealPageType === IDEAL_PAGE_TYPE_REVIEWS) {
        return "Reviews";
    }
    if (idealPageType === IDEAL_PAGE_TYPE_CLIENT_HUB) {
        return "Client Hub";
    }
    return idealPageType || null;
};

const DetailRow = ({ label, children }) => {
    if (children == null || children === "") {
        return null;
    }
    return (
        <div className="mb-3">
            <div className="text-xs font-semibold text-500 uppercase mb-1">{label}</div>
            <div className="text-sm text-900 line-height-3 white-space-normal">{children}</div>
        </div>
    );
};

const formatFileSize = (bytes) => {
    if (bytes == null || Number.isNaN(Number(bytes)) || Number(bytes) < 0) {
        return null;
    }
    const size = Number(bytes);
    if (size < 1024) {
        return `${size} B`;
    }
    const kb = size / 1024;
    if (kb < 1024) {
        return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
    }
    const mb = kb / 1024;
    return `${mb < 10 ? mb.toFixed(2) : mb.toFixed(1)} MB`;
};

const getImageSrc = (item, sessionRevision) => {
    const url = item?.publicUrl || item?.originalPublicUrl || undefined;
    return appendCacheBust(url, getMediaCacheToken(item, sessionRevision));
};

const getMediaSrc = (item, sessionRevision) => {
    const url = item?.publicUrl || item?.originalPublicUrl || undefined;
    return appendCacheBust(url, getMediaCacheToken(item, sessionRevision));
};

const getOriginalVariant = (item) => {
    if (!item?.variants) {
        return null;
    }
    for (let i = 0; i < item.variants.length; i++) {
        if (hasUsedFor(item.variants[i], "original")) {
            return item.variants[i];
        }
    }
    return null;
};

const getConversionSourceUrl = (item, sessionRevision) => {
    let url = null;
    if (item?.variants) {
        for (let i = 0; i < item.variants.length; i++) {
            const variant = item.variants[i];
            if (variant && hasUsedFor(variant, "cs") && variant.publicUrl) {
                url = variant.publicUrl;
                break;
            }
        }
    }
    if (!url) {
        url = item?.originalPublicUrl || null;
    }
    return appendCacheBust(url, getMediaCacheToken(item, sessionRevision)) || null;
};

const USED_FOR_LABELS = {
    original: "Original",
    cs: "Conversion Source",
    as: "Analysis Source",
    display: "Display",
};

const formatUsedFor = (variant) => {
    if (!variant?.usedFor || variant.usedFor.length === 0) {
        return null;
    }
    const labels = [];
    for (let i = 0; i < variant.usedFor.length; i++) {
        const role = variant.usedFor[i];
        labels.push(USED_FOR_LABELS[role] || role);
    }
    return labels.join(", ");
};

const getSortedVariants = (item) => {
    if (!item?.variants || item.variants.length === 0) {
        return [];
    }
    const variants = item.variants.slice();
    variants.sort((left, right) => {
        const leftOriginal = hasUsedFor(left, "original") ? 0 : 1;
        const rightOriginal = hasUsedFor(right, "original") ? 0 : 1;
        if (leftOriginal !== rightOriginal) {
            return leftOriginal - rightOriginal;
        }
        const leftWidth = left?.width != null ? left.width : Number.MAX_SAFE_INTEGER;
        const rightWidth = right?.width != null ? right.width : Number.MAX_SAFE_INTEGER;
        if (leftWidth !== rightWidth) {
            return leftWidth - rightWidth;
        }
        const leftName = left?.s3FileName || "";
        const rightName = right?.s3FileName || "";
        return leftName.localeCompare(rightName);
    });
    return variants;
};

const variantLabel = (variant) => {
    if (hasUsedFor(variant, "original")) {
        return "Original";
    }
    if (variant?.width != null) {
        return `${variant.width}w`;
    }
    if (variant?.format) {
        return String(variant.format).toUpperCase();
    }
    return "Variant";
};

const viewportTemplate = (option) => {
    if (!option) {
        return null;
    }
    return (
        <span className="flex align-items-center gap-2">
            <i className={option.icon} />
            <span>{option.label}</span>
        </span>
    );
};

const JobStock = () => {
    const { displayAlert } = useAppContext();
    const fileUploadRef = useRef(null);
    const actionsMenuRef = useRef(null);
    const [items, setItems] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [lazyState, setLazyState] = useState({
        first: 0,
        rows: 25,
        page: 0,
        sortValue: DEFAULT_SORT.value,
        type: "",
        hasPeople: "",
        includesText: "",
        ethnicity: "",
        propertyType: "",
        idealPageType: "",
        categoryUri: "",
        offerUri: "",
        subcategoryUri: "",
        orientation: "",
        version: "",
        reviewed: "no",
        archived: "no",
        searchText: "",
    });
    const [searchInput, setSearchInput] = useState("");
    const searchTimerRef = useRef(null);
    const searchInputRef = useRef(null);
    const filterDropdownRefs = useRef({});
    const [pendingOpenFilterKey, setPendingOpenFilterKey] = useState(null);
    const [visibleFilterKeys, setVisibleFilterKeys] = useState(DEFAULT_VISIBLE_FILTER_KEYS);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showLightbox, setShowLightbox] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [showCropDialog, setShowCropDialog] = useState(false);
    const [showRegenerateDialog, setShowRegenerateDialog] = useState(false);
    const [regeneratePrompt, setRegeneratePrompt] = useState("");
    const [regenerateProvider, setRegenerateProvider] = useState(AI_IMAGE_PROVIDER_RECRAFT);
    const [showGenerateDialog, setShowGenerateDialog] = useState(false);
    const [generateKind, setGenerateKind] = useState(GENERATE_KIND_HERO);
    const [generateProvider, setGenerateProvider] = useState(AI_IMAGE_PROVIDER_RECRAFT);
    const [generatePrompt, setGeneratePrompt] = useState(DEFAULT_HERO_PROMPT);
    const [generateOffer, setGenerateOffer] = useState(null);
    const [generateOfferLoading, setGenerateOfferLoading] = useState(false);
    const [generateLoading, setGenerateLoading] = useState(false);
    const [showVisionTextDialog, setShowVisionTextDialog] = useState(false);
    const [visionTextTitle, setVisionTextTitle] = useState("");
    const [visionTextDescription, setVisionTextDescription] = useState("");
    const [visionTextSaving, setVisionTextSaving] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [reanalyzeLoading, setReanalyzeLoading] = useState(false);
    const [regenerateLoading, setRegenerateLoading] = useState(false);
    const [reanalyzingIds, setReanalyzingIds] = useState([]);
    const [removingVisionTagKey, setRemovingVisionTagKey] = useState(null);
    const [addingVisionTag, setAddingVisionTag] = useState(false);
    const [categoryToAdd, setCategoryToAdd] = useState("");
    const [flagActionLoading, setFlagActionLoading] = useState(false);
    const [idealPageTypeLoading, setIdealPageTypeLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [previewViewport, setPreviewViewport] = useState("desktop");
    const [previewRevisionById, setPreviewRevisionById] = useState({});
    const previousBusyIdsRef = useRef(new Set());

    const selectedSort = useMemo(() => resolveSortOption(lazyState.sortValue), [lazyState.sortValue]);

    const isFilterVisible = (key) => {
        return visibleFilterKeys.indexOf(key) >= 0;
    };

    const { data: categoryFilterOptions = [{ label: "All categories", value: "" }] } = useQuery(
        ["JobStockCategories"],
        async () => {
            const result = await authApi.get("admin/jobstock/categories");
            return buildCatalogFilterOptions(result.data?.categories, "All categories");
        },
        {
            enabled: isFilterVisible("categoryUri") || selectedItem?.type === "video",
            staleTime: 5 * 60 * 1000,
        }
    );

    const { data: offerFilterOptions = [{ label: "All offers", value: "" }] } = useQuery(
        ["JobStockOffers"],
        async () => {
            const result = await authApi.get("admin/jobstock/offers");
            return buildCatalogFilterOptions(result.data?.offers, "All offers");
        },
        {
            enabled: isFilterVisible("offerUri"),
            staleTime: 5 * 60 * 1000,
        }
    );

    const { data: subcategoryFilterOptions = [{ label: "All subcategories", value: "" }] } = useQuery(
        ["JobStockSubcategories"],
        async () => {
            const result = await authApi.get("admin/jobstock/subcategories");
            return buildCatalogFilterOptions(result.data?.subcategories, "All subcategories");
        },
        {
            enabled: isFilterVisible("subcategoryUri"),
            staleTime: 5 * 60 * 1000,
        }
    );

    const { isLoading, isError, refetch } = useQuery(["JobStock", lazyState], async () => {
        const body = {
            first: lazyState.first,
            rows: lazyState.rows,
            sortField: selectedSort.sortField,
            sortOrder: selectedSort.sortOrder,
        };
        if (lazyState.type) {
            body.type = lazyState.type;
        }
        if (lazyState.hasPeople) {
            body.hasPeople = lazyState.hasPeople;
        }
        if (lazyState.includesText) {
            body.includesText = lazyState.includesText;
        }
        if (lazyState.ethnicity) {
            body.ethnicity = lazyState.ethnicity;
        }
        if (lazyState.propertyType) {
            body.propertyType = lazyState.propertyType;
        }
        if (lazyState.idealPageType) {
            body.idealPageType = lazyState.idealPageType;
        }
        if (lazyState.categoryUri) {
            body.categoryUri = lazyState.categoryUri;
        }
        if (lazyState.offerUri) {
            body.offerUri = lazyState.offerUri;
        }
        if (lazyState.subcategoryUri) {
            body.subcategoryUri = lazyState.subcategoryUri;
        }
        if (lazyState.orientation) {
            body.orientation = lazyState.orientation;
        }
        if (lazyState.version) {
            body.version = lazyState.version;
        }
        if (lazyState.reviewed) {
            body.reviewed = lazyState.reviewed;
        }
        if (lazyState.archived) {
            body.archived = lazyState.archived;
        }
        if (lazyState.searchText) {
            body.searchText = lazyState.searchText;
        }
        const result = await authApi.post("admin/jobstock/list", body);
        setItems(result.data?.items || []);
        setTotalRecords(result.data?.totalRecords || 0);
        return result;
    });

    const mergePolledItem = (polledItem) => {
        if (!polledItem?._id) {
            return;
        }
        setItems((prevItems) => {
            const nextItems = [];
            let replaced = false;
            for (let i = 0; i < prevItems.length; i++) {
                if (prevItems[i]?._id === polledItem._id) {
                    nextItems.push(polledItem);
                    replaced = true;
                } else {
                    nextItems.push(prevItems[i]);
                }
            }
            return replaced ? nextItems : prevItems;
        });
        setSelectedItem((prevSelected) => {
            if (prevSelected?._id === polledItem._id) {
                return polledItem;
            }
            return prevSelected;
        });
    };

    const busyItemIdsKey = useMemo(() => {
        const ids = [];
        for (let i = 0; i < items.length; i++) {
            if (isBusyItem(items[i]) && items[i]._id) {
                ids.push(items[i]._id);
            }
        }
        return ids.join(",");
    }, [items]);

    useEffect(() => {
        const focusTimer = setTimeout(() => {
            if (searchInputRef.current) {
                searchInputRef.current.focus();
            }
        }, 50);
        return () => {
            clearTimeout(focusTimer);
        };
    }, []);

    useEffect(() => {
        if (!pendingOpenFilterKey) {
            return;
        }
        const openTimer = setTimeout(() => {
            const dropdown = filterDropdownRefs.current[pendingOpenFilterKey];
            if (dropdown && typeof dropdown.show === "function") {
                dropdown.show();
            }
            setPendingOpenFilterKey(null);
        }, 0);
        return () => {
            clearTimeout(openTimer);
        };
    }, [pendingOpenFilterKey, visibleFilterKeys]);

    useEffect(() => {
        return () => {
            if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const previousBusyIds = previousBusyIdsRef.current;
        const nextBusyIds = new Set();
        const completedIds = [];
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (!item?._id) {
                continue;
            }
            if (isBusyItem(item)) {
                nextBusyIds.add(item._id);
            } else if (previousBusyIds.has(item._id)) {
                completedIds.push(item._id);
            }
        }
        previousBusyIdsRef.current = nextBusyIds;
        if (completedIds.length === 0) {
            return;
        }
        setPreviewRevisionById((prev) => {
            const next = { ...prev };
            for (let i = 0; i < completedIds.length; i++) {
                const id = completedIds[i];
                next[id] = (prev[id] || 0) + 1;
            }
            return next;
        });
    }, [items]);

    useEffect(() => {
        if (!busyItemIdsKey) {
            return undefined;
        }
        const busyIds = busyItemIdsKey.split(",");
        let cancelled = false;

        const pollBusyItems = async () => {
            for (let i = 0; i < busyIds.length; i++) {
                const id = busyIds[i];
                try {
                    const result = await authApi.get(`admin/jobstock/${id}`);
                    if (cancelled) {
                        return;
                    }
                    if (result.data) {
                        mergePolledItem(result.data);
                    }
                } catch (error) {
                    console.error(error);
                }
            }
        };

        pollBusyItems();
        const timer = setInterval(() => {
            pollBusyItems();
        }, 3000);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [busyItemIdsKey]);

    useEffect(() => {
        if (reanalyzingIds.length === 0) {
            return undefined;
        }
        let cancelled = false;

        const pollReanalyzingItems = async () => {
            const stillPending = [];
            for (let i = 0; i < reanalyzingIds.length; i++) {
                const id = reanalyzingIds[i];
                try {
                    const result = await authApi.get(`admin/jobstock/${id}`);
                    const polledItem = result.data;
                    if (cancelled) {
                        return;
                    }
                    if (polledItem) {
                        mergePolledItem(polledItem);
                        if (getItemVersion(polledItem) < CURRENT_VERSION) {
                            stillPending.push(id);
                        }
                    }
                } catch (error) {
                    console.error(error);
                    if (!cancelled) {
                        stillPending.push(id);
                    }
                }
            }
            if (!cancelled) {
                setReanalyzingIds((prev) => {
                    if (prev.length === stillPending.length) {
                        let same = true;
                        for (let i = 0; i < prev.length; i++) {
                            if (prev[i] !== stillPending[i]) {
                                same = false;
                                break;
                            }
                        }
                        if (same) {
                            return prev;
                        }
                    }
                    return stillPending;
                });
            }
        };

        pollReanalyzingItems();
        const timer = setInterval(() => {
            pollReanalyzingItems();
        }, 3000);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [reanalyzingIds]);

    useEffect(() => {
        if (!selectedItem?._id) {
            return;
        }
        for (let i = 0; i < items.length; i++) {
            if (items[i]?._id === selectedItem._id) {
                setSelectedItem(items[i]);
                break;
            }
        }
    }, [items, selectedItem?._id]);

    const selectedViewport = useMemo(() => {
        for (let i = 0; i < VIEWPORT_OPTIONS.length; i++) {
            if (VIEWPORT_OPTIONS[i].value === previewViewport) {
                return VIEWPORT_OPTIONS[i];
            }
        }
        return VIEWPORT_OPTIONS[2];
    }, [previewViewport]);

    const onPage = (event) => {
        setLazyState({
            ...lazyState,
            first: event.first,
            rows: event.rows,
            page: event.page,
        });
    };

    const onSortChange = (event) => {
        const nextSortValue = event.value || DEFAULT_SORT.value;
        setLazyState({
            ...lazyState,
            first: 0,
            page: 0,
            sortValue: nextSortValue,
        });
    };

    const addFilterField = (key) => {
        setVisibleFilterKeys((prev) => {
            if (prev.indexOf(key) >= 0) {
                return prev;
            }
            const next = [];
            for (let i = 0; i < prev.length; i++) {
                next.push(prev[i]);
            }
            next.push(key);
            return next;
        });
        setPendingOpenFilterKey(key);
    };

    const removeFilterField = (key) => {
        setVisibleFilterKeys((prev) => {
            const next = [];
            for (let i = 0; i < prev.length; i++) {
                if (prev[i] !== key) {
                    next.push(prev[i]);
                }
            }
            return next;
        });
        setLazyState((prev) => {
            return {
                ...prev,
                first: 0,
                page: 0,
                [key]: "",
            };
        });
    };

    const onFilterFieldChange = (key, event) => {
        const value = event.value == null ? "" : event.value;
        setLazyState({
            ...lazyState,
            first: 0,
            page: 0,
            [key]: value,
        });
    };

    const onSearchInputChange = (event) => {
        const value = event.target.value;
        setSearchInput(value);
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
        }
        searchTimerRef.current = setTimeout(() => {
            setLazyState((prev) => {
                return {
                    ...prev,
                    first: 0,
                    page: 0,
                    searchText: (value || "").trim(),
                };
            });
        }, 400);
    };

    const clearSearchText = () => {
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current);
            searchTimerRef.current = null;
        }
        setSearchInput("");
        setLazyState((prev) => {
            return {
                ...prev,
                first: 0,
                page: 0,
                searchText: "",
            };
        });
    };

    const getCatalogFilterOptions = (catalog) => {
        if (catalog === "categories") {
            return categoryFilterOptions;
        }
        if (catalog === "offers") {
            return offerFilterOptions;
        }
        if (catalog === "subcategories") {
            return subcategoryFilterOptions;
        }
        return [{ label: "All", value: "" }];
    };

    const moreFilterOptions = useMemo(() => {
        const options = [];
        for (let i = 0; i < FILTER_FIELDS.length; i++) {
            const field = FILTER_FIELDS[i];
            if (visibleFilterKeys.indexOf(field.key) >= 0) {
                continue;
            }
            options.push({
                label: field.label,
                value: field.key,
            });
        }
        return options;
    }, [visibleFilterKeys]);

    const openLightbox = (item) => {
        setSelectedItem(item);
        setPreviewViewport("desktop");
        setCategoryToAdd("");
        setShowLightbox(true);
    };

    const itemMatchesFlagFilters = (item) => {
        if (!item) {
            return false;
        }
        if (lazyState.reviewed === "yes" && !item.reviewed) {
            return false;
        }
        if (lazyState.reviewed === "no" && item.reviewed) {
            return false;
        }
        if (lazyState.archived === "yes" && !item.archived) {
            return false;
        }
        if (lazyState.archived === "no" && item.archived) {
            return false;
        }
        if (lazyState.idealPageType && item.idealPageType !== lazyState.idealPageType) {
            return false;
        }
        return true;
    };

    const removeItemFromList = (id) => {
        if (!id || !Array.isArray(items)) {
            return;
        }
        let found = false;
        for (let i = 0; i < items.length; i++) {
            if (items[i]?._id === id) {
                found = true;
                break;
            }
        }
        if (!found) {
            return;
        }
        setItems((prev) => {
            if (!Array.isArray(prev)) {
                return prev;
            }
            const next = [];
            for (let i = 0; i < prev.length; i++) {
                if (prev[i]?._id !== id) {
                    next.push(prev[i]);
                }
            }
            return next;
        });
        setTotalRecords((prev) => Math.max(0, (prev || 0) - 1));
    };

    const closeLightbox = (itemOverride) => {
        let closingItem = selectedItem;
        if (itemOverride && typeof itemOverride === "object" && itemOverride._id) {
            closingItem = itemOverride;
        }
        setShowLightbox(false);
        setSelectedItem(null);
        setPreviewViewport("desktop");
        setCategoryToAdd("");
        setShowCropDialog(false);
        if (closingItem?._id && !itemMatchesFlagFilters(closingItem)) {
            removeItemFromList(closingItem._id);
        }
    };

    const handleUpload = async (event) => {
        if (!event.files || event.files.length === 0) {
            return;
        }
        setIsUploading(true);
        const formData = new FormData();
        formData.append("file", event.files[0]);
        try {
            await authApi.post("admin/jobstock/upload", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            displayAlert("success", "Uploaded", "JobStock item uploaded successfully");
            if (fileUploadRef.current) {
                fileUploadRef.current.clear();
            }
            refetch();
        } catch (error) {
            console.error(error);
            const data = error?.response?.data;
            let message = "Please try again later";
            if (typeof data === "string" && data.trim()) {
                message = data;
            } else if (data?.msg) {
                message = data.msg;
            } else if (data?.message) {
                message = data.message;
            }
            displayAlert("error", "Upload Failed", message);
        } finally {
            setIsUploading(false);
        }
    };

    const loadRandomSuggestedOffer = async () => {
        setGenerateOfferLoading(true);
        try {
            const result = await authApi.get("admin/jobstock/random-suggested-offer");
            const offer = result.data || null;
            setGenerateOffer(offer);
            if (offer?.aiPrompt) {
                setGeneratePrompt(offer.aiPrompt);
            }
            return offer;
        } catch (error) {
            console.error(error);
            const data = error?.response?.data;
            let message = "Please try again later";
            if (typeof data === "string" && data.trim()) {
                message = data;
            } else if (data?.msg) {
                message = data.msg;
            } else if (data?.message) {
                message = data.message;
            }
            displayAlert("error", "Random Offer Failed", message);
            return null;
        } finally {
            setGenerateOfferLoading(false);
        }
    };

    const openGenerateDialog = () => {
        setGenerateKind(GENERATE_KIND_HERO);
        setGenerateProvider(AI_IMAGE_PROVIDER_RECRAFT);
        setGeneratePrompt(DEFAULT_HERO_PROMPT);
        setGenerateOffer(null);
        setShowGenerateDialog(true);
    };

    const handleGenerateKindChange = async (kind) => {
        if (!kind || kind === generateKind) {
            return;
        }
        setGenerateKind(kind);
        if (kind === GENERATE_KIND_HERO) {
            setGeneratePrompt(DEFAULT_HERO_PROMPT);
            setGenerateOffer(null);
            return;
        }
        await loadRandomSuggestedOffer();
    };

    const submitGenerateAi = throttle(async () => {
        if (generateLoading) {
            return;
        }
        const prompt = (generatePrompt || "").trim();
        if (!prompt) {
            displayAlert("error", "Generate Failed", "AI prompt is required");
            return;
        }
        if (generateKind === GENERATE_KIND_OFFER_ICON && !generateOffer?.uri) {
            displayAlert("error", "Generate Failed", "Select a suggested offer first");
            return;
        }
        const providers = generateProvider === AI_IMAGE_PROVIDER_BOTH
            ? [AI_IMAGE_PROVIDER_RECRAFT, AI_IMAGE_PROVIDER_BLACK_FOREST_LABS]
            : [
                generateProvider === AI_IMAGE_PROVIDER_BLACK_FOREST_LABS
                    ? AI_IMAGE_PROVIDER_BLACK_FOREST_LABS
                    : AI_IMAGE_PROVIDER_RECRAFT,
            ];
        const baseBody = {
            kind: generateKind,
            aiPrompt: prompt,
        };
        if (generateKind === GENERATE_KIND_OFFER_ICON && generateOffer) {
            baseBody.suggestedOffer = {
                name: generateOffer.name || null,
                uri: generateOffer.uri || null,
                category: generateOffer.category || null,
                categoryUri: generateOffer.categoryUri || null,
                subcategory: generateOffer.subcategory || null,
                subcategoryUri: generateOffer.subcategoryUri || null,
            };
        }
        try {
            setGenerateLoading(true);
            const results = await Promise.all(
                providers.map((provider) =>
                    authApi.post("admin/jobstock/generate-ai", {
                        ...baseBody,
                        aiImageProvider: provider,
                    })
                )
            );
            const createdItems = results
                .map((result) => result.data)
                .filter((item) => !!item);
            if (createdItems.length > 0) {
                setItems((prev) => {
                    if (!Array.isArray(prev)) {
                        return createdItems;
                    }
                    return [...createdItems, ...prev];
                });
                setTotalRecords((prev) => (prev || 0) + createdItems.length);
            }
            setShowGenerateDialog(false);
            displayAlert(
                "success",
                "Generate queued",
                createdItems.length > 1
                    ? "Two JobStock images are being generated (Recraft and Black Forest Labs)"
                    : "A new JobStock image is being generated"
            );
            refetch();
        } catch (error) {
            console.error(error);
            const data = error?.response?.data;
            let message = "Please try again later";
            if (typeof data === "string" && data.trim()) {
                message = data;
            } else if (data?.msg) {
                message = data.msg;
            } else if (data?.message) {
                message = data.message;
            }
            displayAlert("error", "Generate Failed", message);
            refetch();
        } finally {
            setGenerateLoading(false);
        }
    }, 500, { trailing: false });

    const deleteItem = async () => {
        if (!selectedItem?._id) {
            return;
        }
        try {
            setDeleteLoading(true);
            await authApi.delete(`admin/jobstock/${selectedItem._id}`);
            displayAlert("success", "Deleted", "JobStock item deleted successfully");
            setShowDeleteDialog(false);
            closeLightbox();
            refetch();
        } catch (error) {
            console.error(error);
            const data = error?.response?.data;
            let message = "Please try again later";
            if (typeof data === "string" && data.trim()) {
                message = data;
            } else if (data?.msg) {
                message = data.msg;
            } else if (data?.message) {
                message = data.message;
            }
            displayAlert("error", "Delete Failed", message);
        } finally {
            setDeleteLoading(false);
        }
    };

    const reanalyzeItem = async () => {
        if (!selectedItem?._id) {
            return;
        }
        const id = selectedItem._id;
        try {
            setReanalyzeLoading(true);
            await authApi.post(`admin/jobstock/${id}/reanalyze`);
            setReanalyzingIds((prev) => {
                if (prev.indexOf(id) >= 0) {
                    return prev;
                }
                return prev.concat([id]);
            });
            displayAlert("success", "Re-analyze queued", "Vision will refresh with people and ethnicity tags");
            refetch();
        } catch (error) {
            console.error(error);
            const data = error?.response?.data;
            let message = "Please try again later";
            if (typeof data === "string" && data.trim()) {
                message = data;
            } else if (data?.msg) {
                message = data.msg;
            } else if (data?.message) {
                message = data.message;
            }
            displayAlert("error", "Re-analyze Failed", message);
        } finally {
            setReanalyzeLoading(false);
        }
    };

    const regenerateAiItem = throttle(async () => {
        if (!selectedItem?._id || regenerateLoading) {
            return;
        }
        const prompt = (regeneratePrompt || "").trim();
        if (!prompt) {
            displayAlert("error", "Regenerate Failed", "AI prompt is required");
            return;
        }
        const provider = regenerateProvider === AI_IMAGE_PROVIDER_BLACK_FOREST_LABS
            ? AI_IMAGE_PROVIDER_BLACK_FOREST_LABS
            : AI_IMAGE_PROVIDER_RECRAFT;
        try {
            setRegenerateLoading(true);
            const result = await authApi.post(`admin/jobstock/${selectedItem._id}/regenerate-ai`, {
                aiPrompt: prompt,
                aiImageProvider: provider,
            });
            const updated = result.data;
            if (updated) {
                setSelectedItem(updated);
                setItems((prev) => {
                    if (!Array.isArray(prev)) {
                        return prev;
                    }
                    return prev.map((item) => (item?._id === updated._id ? updated : item));
                });
            } else {
                const optimisticItem = {
                    ...selectedItem,
                    aiPrompt: prompt,
                    aiImageProvider: provider,
                    status: "processing",
                    aiGenerating: true,
                    reviewed: false,
                };
                setSelectedItem(optimisticItem);
                setItems((prev) => {
                    if (!Array.isArray(prev)) {
                        return prev;
                    }
                    return prev.map((item) => (item?._id === optimisticItem._id ? optimisticItem : item));
                });
            }
            setShowRegenerateDialog(false);
            displayAlert("success", "Regenerate queued", "A new AI image will replace the files for this JobStock id");
            refetch();
        } catch (error) {
            console.error(error);
            const data = error?.response?.data;
            let message = "Please try again later";
            if (typeof data === "string" && data.trim()) {
                message = data;
            } else if (data?.msg) {
                message = data.msg;
            } else if (data?.message) {
                message = data.message;
            }
            displayAlert("error", "Regenerate Failed", message);
        } finally {
            setRegenerateLoading(false);
        }
    }, 500, { trailing: false });

    const removeVisionTag = async (kind, uri) => {
        if (!selectedItem?._id || !kind || !uri || removingVisionTagKey) {
            return;
        }
        const key = `${kind}:${uri}`;
        const previousItem = selectedItem;
        const previousList = items;
        const nextVision = {
            ...(selectedItem.vision || {}),
            [kind]: (selectedItem.vision?.[kind] || []).filter((entry) => entry?.uri !== uri),
        };
        const optimisticItem = {
            ...selectedItem,
            vision: nextVision,
            reviewed: true,
        };
        setSelectedItem(optimisticItem);
        setItems((prev) => {
            if (!Array.isArray(prev)) {
                return prev;
            }
            return prev.map((item) => (item?._id === optimisticItem._id ? optimisticItem : item));
        });
        setRemovingVisionTagKey(key);
        try {
            const result = await authApi.post(`admin/jobstock/${selectedItem._id}/vision/remove-tag`, {
                kind,
                uri,
            });
            const updated = result.data;
            if (updated) {
                setSelectedItem(updated);
                setItems((prev) => {
                    if (!Array.isArray(prev)) {
                        return prev;
                    }
                    return prev.map((item) => (item?._id === updated._id ? updated : item));
                });
            }
        } catch (error) {
            console.error(error);
            setSelectedItem(previousItem);
            setItems(previousList);
            const data = error?.response?.data;
            let message = "Please try again later";
            if (typeof data === "string" && data.trim()) {
                message = data;
            } else if (data?.msg) {
                message = data.msg;
            } else if (data?.message) {
                message = data.message;
            }
            displayAlert("error", "Remove Tag Failed", message);
        } finally {
            setRemovingVisionTagKey(null);
        }
    };

    const openVisionTextDialog = () => {
        if (!selectedItem) {
            return;
        }
        setVisionTextTitle(selectedItem.vision?.title || "");
        setVisionTextDescription(selectedItem.vision?.description || "");
        setShowVisionTextDialog(true);
    };

    const saveVisionText = throttle(async () => {
        if (!selectedItem?._id || visionTextSaving) {
            return;
        }
        setVisionTextSaving(true);
        try {
            const result = await authApi.post(`admin/jobstock/${selectedItem._id}/vision/update-text`, {
                title: visionTextTitle,
                description: visionTextDescription,
            });
            const updated = result.data;
            if (updated) {
                setSelectedItem(updated);
                setItems((prev) => {
                    if (!Array.isArray(prev)) {
                        return prev;
                    }
                    return prev.map((item) => (item?._id === updated._id ? updated : item));
                });
            }
            setShowVisionTextDialog(false);
            displayAlert("success", "Updated", "Title and description saved");
        } catch (error) {
            console.error(error);
            const data = error?.response?.data;
            let message = "Please try again later";
            if (typeof data === "string" && data.trim()) {
                message = data;
            } else if (data?.msg) {
                message = data.msg;
            } else if (data?.message) {
                message = data.message;
            }
            displayAlert("error", "Update Failed", message);
        } finally {
            setVisionTextSaving(false);
        }
    }, 500, { trailing: false });

    const addVisionCategory = async () => {
        if (!selectedItem?._id || !categoryToAdd || addingVisionTag || removingVisionTagKey) {
            return;
        }
        let categoryName = null;
        for (let i = 0; i < categoryFilterOptions.length; i++) {
            if (categoryFilterOptions[i].value === categoryToAdd) {
                categoryName = categoryFilterOptions[i].label;
                break;
            }
        }
        if (!categoryName) {
            return;
        }
        const existing = selectedItem.vision?.categories || [];
        for (let i = 0; i < existing.length; i++) {
            if (existing[i]?.uri === categoryToAdd) {
                setCategoryToAdd("");
                return;
            }
        }
        const previousItem = selectedItem;
        const previousList = items;
        const nextTag = {
            uri: categoryToAdd,
            name: categoryName,
            confidence: 1,
        };
        const optimisticItem = {
            ...selectedItem,
            vision: {
                ...(selectedItem.vision || {}),
                categories: [...existing, nextTag],
            },
            reviewed: true,
        };
        setSelectedItem(optimisticItem);
        setItems((prev) => {
            if (!Array.isArray(prev)) {
                return prev;
            }
            return prev.map((item) => (item?._id === optimisticItem._id ? optimisticItem : item));
        });
        setCategoryToAdd("");
        setAddingVisionTag(true);
        try {
            const result = await authApi.post(`admin/jobstock/${selectedItem._id}/vision/add-tag`, {
                kind: "categories",
                uri: nextTag.uri,
                name: nextTag.name,
            });
            const updated = result.data;
            if (updated) {
                setSelectedItem(updated);
                setItems((prev) => {
                    if (!Array.isArray(prev)) {
                        return prev;
                    }
                    return prev.map((item) => (item?._id === updated._id ? updated : item));
                });
            }
        } catch (error) {
            console.error(error);
            setSelectedItem(previousItem);
            setItems(previousList);
            const data = error?.response?.data;
            let message = "Please try again later";
            if (typeof data === "string" && data.trim()) {
                message = data;
            } else if (data?.msg) {
                message = data.msg;
            } else if (data?.message) {
                message = data.message;
            }
            displayAlert("error", "Add Category Failed", message);
        } finally {
            setAddingVisionTag(false);
        }
    };

    const applyFlagUpdate = (updated) => {
        if (!updated) {
            return;
        }
        setSelectedItem(updated);
        setItems((prev) => {
            if (!Array.isArray(prev)) {
                return prev;
            }
            return prev.map((item) => (item?._id === updated._id ? updated : item));
        });
    };

    const updateIdealPageType = async (nextIdealPageType) => {
        if (!selectedItem?._id || idealPageTypeLoading) {
            return;
        }
        const previousValue = selectedItem.idealPageType || "";
        const nextValue = nextIdealPageType || "";
        if (previousValue === nextValue) {
            return;
        }
        const previousItem = selectedItem;
        const previousList = items;
        const optimisticItem = {
            ...selectedItem,
            idealPageType: nextValue || null,
        };
        applyFlagUpdate(optimisticItem);
        setIdealPageTypeLoading(true);
        try {
            const result = await authApi.post(`admin/jobstock/${selectedItem._id}/ideal-page-type`, {
                idealPageType: nextValue || null,
            });
            const updated = result.data || optimisticItem;
            applyFlagUpdate(updated);
        } catch (error) {
            console.error(error);
            setSelectedItem(previousItem);
            setItems(previousList);
            const data = error?.response?.data;
            let message = "Please try again later";
            if (typeof data === "string" && data.trim()) {
                message = data;
            } else if (data?.msg) {
                message = data.msg;
            } else if (data?.message) {
                message = data.message;
            }
            displayAlert("error", "Update Ideal Page Failed", message);
        } finally {
            setIdealPageTypeLoading(false);
        }
    };

    const markReviewed = async () => {
        if (!selectedItem?._id || selectedItem.reviewed || flagActionLoading) {
            return;
        }
        const previousItem = selectedItem;
        const previousList = items;
        const optimisticItem = {
            ...selectedItem,
            reviewed: true,
        };
        applyFlagUpdate(optimisticItem);
        setFlagActionLoading(true);
        try {
            const result = await authApi.post(`admin/jobstock/${selectedItem._id}/reviewed`);
            const updated = result.data || optimisticItem;
            applyFlagUpdate(updated);
            closeLightbox(updated);
        } catch (error) {
            console.error(error);
            setSelectedItem(previousItem);
            setItems(previousList);
            const data = error?.response?.data;
            let message = "Please try again later";
            if (typeof data === "string" && data.trim()) {
                message = data;
            } else if (data?.msg) {
                message = data.msg;
            } else if (data?.message) {
                message = data.message;
            }
            displayAlert("error", "Mark Reviewed Failed", message);
        } finally {
            setFlagActionLoading(false);
        }
    };

    const clearIncludesTextAndReview = async () => {
        if (!selectedItem?._id || flagActionLoading) {
            return;
        }
        if (selectedItem.vision?.includesText !== "yes") {
            return;
        }
        const previousItem = selectedItem;
        const previousList = items;
        const optimisticItem = {
            ...selectedItem,
            reviewed: true,
            vision: {
                ...(selectedItem.vision || {}),
                includesText: "no",
            },
        };
        applyFlagUpdate(optimisticItem);
        setFlagActionLoading(true);
        try {
            const result = await authApi.post(
                `admin/jobstock/${selectedItem._id}/vision/clear-includes-text`
            );
            const updated = result.data || optimisticItem;
            applyFlagUpdate(updated);
            closeLightbox(updated);
        } catch (error) {
            console.error(error);
            setSelectedItem(previousItem);
            setItems(previousList);
            const data = error?.response?.data;
            let message = "Please try again later";
            if (typeof data === "string" && data.trim()) {
                message = data;
            } else if (data?.msg) {
                message = data.msg;
            } else if (data?.message) {
                message = data.message;
            }
            displayAlert("error", "Clear Text Flag Failed", message);
        } finally {
            setFlagActionLoading(false);
        }
    };

    const setArchivedFlag = async (archived) => {
        if (!selectedItem?._id || flagActionLoading) {
            return;
        }
        const alreadyArchived = !!selectedItem.archived;
        if (archived === alreadyArchived) {
            return;
        }
        const previousItem = selectedItem;
        const previousList = items;
        const optimisticItem = {
            ...selectedItem,
            archived,
        };
        applyFlagUpdate(optimisticItem);
        setFlagActionLoading(true);
        const path = archived ? "archive" : "unarchive";
        try {
            const result = await authApi.post(`admin/jobstock/${selectedItem._id}/${path}`);
            const updated = result.data || optimisticItem;
            applyFlagUpdate(updated);
            if (archived) {
                closeLightbox(updated);
            }
        } catch (error) {
            console.error(error);
            setSelectedItem(previousItem);
            setItems(previousList);
            const data = error?.response?.data;
            let message = "Please try again later";
            if (typeof data === "string" && data.trim()) {
                message = data;
            } else if (data?.msg) {
                message = data.msg;
            } else if (data?.message) {
                message = data.message;
            }
            displayAlert("error", archived ? "Archive Failed" : "Unarchive Failed", message);
        } finally {
            setFlagActionLoading(false);
        }
    };

    const isVideo = (item) => {
        return item?.type === "video";
    };

    const isReanalyzing = (item) => {
        if (!item?._id) {
            return false;
        }
        return reanalyzingIds.indexOf(item._id) >= 0;
    };

    const pageHeader = (
        <PageHeading pageTitle="Admin - JobStock">
            <div className="flex align-items-center gap-2">
                <Button
                    label="Generate"
                    icon="pi pi-sparkles"
                    className="p-button-outlined"
                    onClick={openGenerateDialog}
                    disabled={isUploading || generateLoading}
                />
                <FileUpload
                    ref={fileUploadRef}
                    mode="basic"
                    name="file"
                    accept="image/*,.heic,.heif,image/heic,image/heif,video/mp4"
                    chooseLabel="Upload"
                    customUpload
                    auto
                    uploadHandler={handleUpload}
                    disabled={isUploading || generateLoading}
                />
            </div>
        </PageHeading>
    );

    const filterBar = (
        <div className="flex flex-row flex-wrap align-items-center justify-content-between gap-2 mb-3">
            <div className="flex flex-row flex-wrap align-items-center gap-2 flex-grow-1">
                <div className="font-bold white-space-nowrap">Filter by</div>
                <InputText
                    ref={searchInputRef}
                    value={searchInput}
                    onChange={onSearchInputChange}
                    placeholder="Search..."
                    aria-label="Search JobStock"
                    style={{ width: "18rem", maxWidth: "100%" }}
                />
                {searchInput ? (
                    <Button
                        type="button"
                        icon="pi pi-times"
                        className="p-button-outlined p-button-rounded p-button-sm"
                        onClick={clearSearchText}
                        aria-label="Clear search"
                        tooltip="Clear search"
                        tooltipOptions={{ position: "top" }}
                    />
                ) : null}
                {visibleFilterKeys.map((key) => {
                    const field = findFilterField(key);
                    if (!field) {
                        return null;
                    }
                    const options = field.catalog
                        ? getCatalogFilterOptions(field.catalog)
                        : field.options;
                    return (
                        <div key={key} className="flex align-items-center gap-1">
                            <Dropdown
                                ref={(el) => {
                                    if (el) {
                                        filterDropdownRefs.current[key] = el;
                                    } else {
                                        delete filterDropdownRefs.current[key];
                                    }
                                }}
                                value={lazyState[key]}
                                options={options}
                                onChange={(event) => onFilterFieldChange(key, event)}
                                optionLabel="label"
                                optionValue="value"
                                placeholder={field.placeholder}
                                filter={!!field.filter}
                                showClear={!!field.showClear}
                                aria-label={`Filter by ${field.label.toLowerCase()}`}
                            />
                            <Button
                                type="button"
                                icon="pi pi-times"
                                className="p-button-text p-button-rounded p-button-sm"
                                onClick={() => removeFilterField(key)}
                                aria-label={`Remove ${field.label} filter`}
                                tooltip={`Remove ${field.label}`}
                                tooltipOptions={{ position: "top" }}
                            />
                        </div>
                    );
                })}
                {moreFilterOptions.length > 0 ? (
                    <Dropdown
                        value=""
                        options={moreFilterOptions}
                        onChange={(event) => {
                            if (event.value) {
                                addFilterField(event.value);
                            }
                        }}
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Filter..."
                        className="w-10rem"
                        aria-label="Add filter"
                    />
                ) : null}
                <Button
                    icon="pi pi-refresh"
                    className="p-button-outlined"
                    onClick={() => refetch()}
                    disabled={isLoading || isUploading}
                    aria-label="Refresh"
                    tooltip="Refresh"
                    tooltipOptions={{ position: "top" }}
                />
            </div>
            <div className="flex align-items-center gap-2 ml-auto">
                <div className="font-bold white-space-nowrap">Sort by</div>
                <Dropdown
                    value={lazyState.sortValue}
                    options={SORT_OPTIONS}
                    onChange={onSortChange}
                    optionLabel="label"
                    optionValue="value"
                    placeholder="Sort by"
                    aria-label="Sort JobStock items"
                />
            </div>
        </div>
    );

    if (isError) {
        return (
            <div>
                {pageHeader}
                <div className="layout-content">
                    <PageLoadError />
                </div>
            </div>
        );
    }

    const originalVariant = selectedItem ? getOriginalVariant(selectedItem) : null;
    const originalFileSize = formatFileSize(originalVariant?.size);
    const selectedVariants = selectedItem ? getSortedVariants(selectedItem) : [];
    const cropSourceUrl = selectedItem
        ? getConversionSourceUrl(selectedItem, previewRevisionById[selectedItem._id])
        : null;
    const canCrop = selectedItem
        && !isVideo(selectedItem)
        && !isBusyItem(selectedItem)
        && !!cropSourceUrl;
    const canReanalyze = selectedItem
        && needsReanalyze(selectedItem)
        && !isBusyItem(selectedItem)
        && !isReanalyzing(selectedItem);
    const reanalyzingSelected = selectedItem ? isReanalyzing(selectedItem) : false;
    const canRegenerateAi = selectedItem
        && !isVideo(selectedItem)
        && !!selectedItem.aiPrompt
        && !isBusyItem(selectedItem)
        && !regenerateLoading;

    const actionMenuItems = [];
    if (selectedItem) {
        if (selectedItem.originalPublicUrl) {
            actionMenuItems.push({
                label: "Download",
                icon: "pi pi-download",
                url: selectedItem.originalPublicUrl,
                target: "_blank",
            });
        }
        if (canCrop) {
            actionMenuItems.push({
                label: "Crop",
                icon: "pi pi-pencil",
                command: () => setShowCropDialog(true),
            });
        }
        if (canRegenerateAi || regenerateLoading) {
            actionMenuItems.push({
                label: regenerateLoading ? "Regenerating…" : "Regenerate with AI",
                icon: "pi pi-sparkles",
                disabled: !canRegenerateAi || regenerateLoading,
                command: () => {
                    if (canRegenerateAi && !regenerateLoading) {
                        setRegeneratePrompt(selectedItem.aiPrompt || "");
                        setRegenerateProvider(resolveAiImageProvider(selectedItem));
                        setShowRegenerateDialog(true);
                    }
                },
            });
        }
        if (canReanalyze || reanalyzingSelected) {
            actionMenuItems.push({
                label: reanalyzingSelected || reanalyzeLoading ? "Re-analyzing…" : "Re-analyze",
                icon: "pi pi-refresh",
                disabled: !canReanalyze || reanalyzeLoading,
                command: () => {
                    if (canReanalyze && !reanalyzeLoading) {
                        reanalyzeItem();
                    }
                },
            });
        }
        if (selectedItem.archived) {
            actionMenuItems.push({
                label: "Unarchive",
                icon: "pi pi-folder-open",
                disabled: flagActionLoading,
                command: () => {
                    if (!flagActionLoading) {
                        setArchivedFlag(false);
                    }
                },
            });
        } else {
            actionMenuItems.push({
                label: "Archive",
                icon: "pi pi-inbox",
                disabled: flagActionLoading,
                command: () => {
                    if (!flagActionLoading) {
                        setArchivedFlag(true);
                    }
                },
            });
        }
        if (isDevelopmentEnvironment) {
            actionMenuItems.push({
                label: "Delete",
                icon: "pi pi-trash",
                command: () => setShowDeleteDialog(true),
            });
        }
    }

    const lightboxHeader = (
        <div className="flex align-items-center justify-content-between gap-3 w-full pr-2" style={{ minWidth: 0 }}>
            <span className="font-semibold white-space-nowrap overflow-hidden text-overflow-ellipsis">
                {selectedItem?.vision?.title || selectedItem?._id || "JobStock"}
            </span>
            {selectedItem ? (
                <div className="flex align-items-center gap-2 flex-shrink-0">
                    {selectedItem.vision?.includesText === "yes" ? (
                        <Button
                            label="No noticeable text"
                            icon="pi pi-eye-slash"
                            className="p-button-sm p-button-outlined"
                            onClick={clearIncludesTextAndReview}
                            loading={flagActionLoading}
                            disabled={flagActionLoading}
                            tooltip="Set includes text to no and mark reviewed"
                            tooltipOptions={{ position: "bottom" }}
                        />
                    ) : null}
                    {!selectedItem.reviewed ? (
                        <Button
                            label="Reviewed"
                            icon="pi pi-check"
                            className="p-button-sm flex-shrink-0"
                            onClick={markReviewed}
                            loading={flagActionLoading}
                            disabled={flagActionLoading}
                        />
                    ) : null}
                    <Menu
                        model={actionMenuItems}
                        popup
                        ref={actionsMenuRef}
                        className="white-space-nowrap"
                        style={{ minWidth: "12rem" }}
                    />
                    <Button
                        label="Actions"
                        icon="pi pi-ellipsis-v"
                        iconPos="right"
                        className="p-button-outlined p-button-sm flex-shrink-0"
                        onClick={(event) => actionsMenuRef.current && actionsMenuRef.current.toggle(event)}
                        aria-haspopup
                        aria-controls="jobstock_actions_menu"
                        loading={reanalyzeLoading || reanalyzingSelected || flagActionLoading || regenerateLoading}
                    />
                </div>
            ) : null}
        </div>
    );

    const lightboxSidePanel = selectedItem ? (
        <aside
            className="flex flex-column surface-ground border-left-1 surface-border"
            style={{ width: "500px", minWidth: "500px", maxWidth: "500px", height: "100%" }}
        >
            <div className="p-3 border-bottom-1 surface-border">
                <div className="text-xs font-semibold text-500 uppercase mb-2">Preview size</div>
                {!isVideo(selectedItem) && !isBusyItem(selectedItem) ? (
                    <SelectButton
                        value={previewViewport}
                        options={VIEWPORT_OPTIONS}
                        onChange={(event) => {
                            if (event.value) {
                                setPreviewViewport(event.value);
                            }
                        }}
                        itemTemplate={viewportTemplate}
                        optionLabel="label"
                        optionValue="value"
                        className="w-full jobstock-selectbutton"
                    />
                ) : (
                    <div className="text-sm text-600">Not available for this item</div>
                )}
            </div>

            <div className="p-3 flex-1 overflow-auto">
                <div className="text-xs font-semibold text-500 uppercase mb-3 flex align-items-center gap-1">
                    <span>Vision</span>
                    <Button
                        type="button"
                        icon="pi pi-pencil"
                        className="p-button-text p-button-rounded p-button-sm"
                        onClick={openVisionTextDialog}
                        disabled={isBusyItem(selectedItem) || visionTextSaving}
                        tooltip="Edit title and description"
                        tooltipOptions={{ position: "top" }}
                        aria-label="Edit title and description"
                    />
                </div>
                <DetailRow label="Title">
                    {selectedItem.vision?.title || <span className="text-500">—</span>}
                </DetailRow>
                <DetailRow label="Description">
                    {selectedItem.vision?.description || <span className="text-500">—</span>}
                </DetailRow>
                <RemovableCatalogTags
                    label="Categories"
                    kind="categories"
                    items={selectedItem.vision?.categories}
                    removingKey={removingVisionTagKey}
                    onRemove={removeVisionTag}
                    allowEmpty={isVideo(selectedItem)}
                    footer={isVideo(selectedItem) ? (
                        <div className="flex flex-wrap align-items-center gap-2 mt-2">
                            <Dropdown
                                value={categoryToAdd}
                                options={categoryFilterOptions.filter((option) => {
                                    if (!option?.value) {
                                        return false;
                                    }
                                    const tagged = selectedItem.vision?.categories || [];
                                    for (let i = 0; i < tagged.length; i++) {
                                        if (tagged[i]?.uri === option.value) {
                                            return false;
                                        }
                                    }
                                    return true;
                                })}
                                onChange={(event) => setCategoryToAdd(event.value || "")}
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Add category"
                                className="w-16rem"
                                filter
                                disabled={addingVisionTag || !!removingVisionTagKey}
                            />
                            <Button
                                label="Add"
                                icon="pi pi-plus"
                                className="p-button-sm"
                                disabled={!categoryToAdd || addingVisionTag || !!removingVisionTagKey}
                                loading={addingVisionTag}
                                onClick={addVisionCategory}
                            />
                        </div>
                    ) : null}
                />
                {!isVideo(selectedItem) && (
                    <>
                        <RemovableCatalogTags
                            label="Subcategories"
                            kind="subcategories"
                            items={selectedItem.vision?.subcategories}
                            removingKey={removingVisionTagKey}
                            onRemove={removeVisionTag}
                        />
                        <RemovableCatalogTags
                            label="Offers"
                            kind="offers"
                            items={selectedItem.vision?.offers}
                            removingKey={removingVisionTagKey}
                            onRemove={removeVisionTag}
                        />
                        <DetailRow label="Property type">{formatPropertyType(selectedItem.vision?.propertyType)}</DetailRow>
                        {selectedItem.vision?.includesText != null && (
                            <DetailRow label="Includes text (Claude)">
                                <span className="flex align-items-center gap-2 flex-wrap">
                                    <span>{selectedItem.vision.includesText}</span>
                                    {selectedItem.vision.includesText === "yes" ? (
                                        <Button
                                            label="Set to no"
                                            className="p-button-text p-button-sm p-0"
                                            onClick={clearIncludesTextAndReview}
                                            loading={flagActionLoading}
                                            disabled={flagActionLoading}
                                        />
                                    ) : null}
                                </span>
                            </DetailRow>
                        )}
                        {selectedItem.vision?.hasPeople != null && (
                            <DetailRow label="Has people (Claude)">{selectedItem.vision.hasPeople}</DetailRow>
                        )}
                        {selectedItem.vision?.personCount != null && (
                            <DetailRow label="Person count (Claude)">{selectedItem.vision.personCount}</DetailRow>
                        )}
                        <DetailRow label="Ethnicities (Claude)">{formatEthnicities(selectedItem)}</DetailRow>
                        <DetailRow label="Location specific">
                            {formatLocationSpecific(selectedItem.vision?.locationSpecific)
                                || <span className="text-500">—</span>}
                        </DetailRow>
                        {selectedItem.vision?.locationSpecific === true && (
                            <DetailRow label="State codes">
                                {formatStateCodes(selectedItem.vision?.stateCodes)
                                    || <span className="text-500">—</span>}
                            </DetailRow>
                        )}
                    </>
                )}

                <div className="mt-2 pt-3 border-top-1 surface-border">
                    <div className="text-xs font-semibold text-500 uppercase mb-3">Details</div>
                    <DetailRow label="ID">{selectedItem._id}</DetailRow>
                    <DetailRow label="Type">{selectedItem.type}</DetailRow>
                    <DetailRow label="Status">{selectedItem.status}</DetailRow>
                    <div className="flex gap-2 flex-wrap mb-3">
                        {selectedItem.reviewed ? (
                            <Chip label="Reviewed" icon="pi pi-check" className="text-sm" />
                        ) : (
                            <Chip label="Not reviewed" className="text-sm" />
                        )}
                        {selectedItem.archived ? (
                            <Chip label="Archived" icon="pi pi-inbox" className="text-sm" />
                        ) : (
                            <Chip label="Active" className="text-sm" />
                        )}
                        {selectedItem.idealPageType ? (
                            <Chip
                                label={formatIdealPageType(selectedItem.idealPageType)}
                                icon="pi pi-file"
                                className="text-sm"
                            />
                        ) : null}
                    </div>
                    <DetailRow label="Ideal page">
                        <Dropdown
                            value={selectedItem.idealPageType || ""}
                            options={IDEAL_PAGE_TYPE_EDIT_OPTIONS}
                            onChange={(event) => updateIdealPageType(event.value || "")}
                            optionLabel="label"
                            optionValue="value"
                            placeholder="None"
                            className="w-16rem"
                            disabled={idealPageTypeLoading}
                        />
                    </DetailRow>
                    <DetailRow label="Version">
                        {getItemVersion(selectedItem)}
                        {getItemVersion(selectedItem) < CURRENT_VERSION ? ` (current is ${CURRENT_VERSION})` : ""}
                    </DetailRow>
                    {selectedItem.width != null && selectedItem.height != null && (
                        <DetailRow label="Dimensions">
                            {selectedItem.width}×{selectedItem.height}px
                        </DetailRow>
                    )}
                    <DetailRow label="File size">{originalFileSize}</DetailRow>
                    <DetailRow label="Aspect ratio">{selectedItem.aspectRatioLabel}</DetailRow>
                    <DetailRow label="Orientation">{selectedItem.orientation}</DetailRow>
                    {selectedItem.hasAlpha != null && (
                        <DetailRow label="Has alpha">{selectedItem.hasAlpha ? "Yes" : "No"}</DetailRow>
                    )}
                    <DetailRow label="Original format">{originalVariant?.format}</DetailRow>
                    <DetailRow label="AI context">{selectedItem.aiContext}</DetailRow>
                    <DetailRow label="AI prompt">{selectedItem.aiPrompt}</DetailRow>
                    <DetailRow label="AI image provider">{formatAiImageProvider(selectedItem.aiImageProvider)}</DetailRow>
                    <DetailRow label="Usage count">{selectedItem.usageCount ?? 0}</DetailRow>
                    <DetailRow label="Replaced count">{selectedItem.replacedCount ?? 0}</DetailRow>
                </div>

                {selectedItem.processingError && (
                    <div className="mt-2 pt-3 border-top-1 surface-border">
                        <DetailRow label="Processing error">
                            <span className="text-red-500">{selectedItem.processingError}</span>
                        </DetailRow>
                    </div>
                )}

                {getLatestVisionAnalysisError(selectedItem) && (
                    <div className="mt-2 pt-3 border-top-1 surface-border">
                        <DetailRow label="Vision analysis error">
                            <span className="text-red-500">{getLatestVisionAnalysisError(selectedItem)}</span>
                        </DetailRow>
                    </div>
                )}

                {selectedVariants.length > 0 && (
                    <div className="mt-2 pt-3 border-top-1 surface-border">
                        <div className="text-xs font-semibold text-500 uppercase mb-3">Variants</div>
                        <Accordion multiple>
                            {selectedVariants.map((variant, index) => {
                                const sizeLabel = formatFileSize(variant.size);
                                const header = sizeLabel
                                    ? `${variantLabel(variant)} · ${sizeLabel}`
                                    : variantLabel(variant);
                                return (
                                    <AccordionTab
                                        key={(variant.s3FileName || variant.publicUrl || "variant") + "-" + index}
                                        header={header}
                                    >
                                        <DetailRow label="Used for">{formatUsedFor(variant)}</DetailRow>
                                        {variant.width != null && variant.height != null && (
                                            <DetailRow label="Dimensions">
                                                {variant.width}×{variant.height}px
                                            </DetailRow>
                                        )}
                                        {variant.width != null && variant.height == null && (
                                            <DetailRow label="Width">{variant.width}px</DetailRow>
                                        )}
                                        <DetailRow label="Format">{variant.format}</DetailRow>
                                        <DetailRow label="Content type">{variant.contentType}</DetailRow>
                                        <DetailRow label="File size">{sizeLabel}</DetailRow>
                                        <DetailRow label="S3 key">{variant.s3FileName}</DetailRow>
                                    </AccordionTab>
                                );
                            })}
                        </Accordion>
                    </div>
                )}
            </div>
        </aside>
    ) : null;

    return (
        <div>
            <style>{`
                .jobstock-selectbutton.p-selectbutton .p-button {
                    background: #ffffff;
                    border-color: var(--surface-border, #dee2e6);
                    color: var(--text-color, #495057);
                    font-weight: 500;
                }
                .jobstock-selectbutton.p-selectbutton .p-button:not(.p-highlight):not(.p-disabled):hover {
                    background: var(--surface-100, #f8f9fa);
                    border-color: var(--primary-color, #0d6efd);
                    color: var(--primary-color, #0d6efd);
                }
                .jobstock-selectbutton.p-selectbutton .p-button.p-highlight {
                    background: var(--primary-color, #0d6efd);
                    border-color: var(--primary-color, #0d6efd);
                    color: var(--primary-color-text, #ffffff);
                    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.2);
                }
                .jobstock-selectbutton.p-selectbutton .p-button.p-highlight:hover {
                    background: var(--primary-600, #0b5ed7);
                    border-color: var(--primary-600, #0b5ed7);
                    color: var(--primary-color-text, #ffffff);
                }
                .jobstock-selectbutton.p-selectbutton .p-button.p-disabled {
                    opacity: 0.65;
                }
            `}</style>
            {pageHeader}
            <div className="layout-content">
                <div className="card">
                    {filterBar}
                    {(isLoading || isUploading) && (
                        <div className="flex justify-content-center p-4">
                            <ProgressSpinner style={{ width: "50px", height: "50px" }} />
                        </div>
                    )}

                    {!isLoading && items.length === 0 && (
                        <div className="text-center text-700 p-5">
                            No JobStock items yet. Upload an image or video to get started.
                        </div>
                    )}

                    {!isLoading && items.length > 0 && (
                        <>
                            <div className="grid">
                                {items.map((item) => (
                                    <div key={item._id} className="col-6 sm:col-4 md:col-3">
                                        <button
                                            type="button"
                                            className="p-0 border-none border-round overflow-hidden cursor-pointer w-full surface-100 relative"
                                            style={{ aspectRatio: "1 / 1", display: "block", background: "transparent" }}
                                            onClick={() => openLightbox(item)}
                                        >
                                            {isBusyItem(item) ? (
                                                <div aria-busy="true" style={{ width: "100%", height: "100%" }}>
                                                    <Skeleton width="100%" height="100%" className="jobstock-processing-skeleton" />
                                                </div>
                                            ) : isVideo(item) ? (
                                                <>
                                                    <video
                                                        src={getMediaSrc(item, previewRevisionById[item._id])}
                                                        muted
                                                        playsInline
                                                        preload="metadata"
                                                        style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                                                    />
                                                    <span
                                                        className="absolute flex align-items-center justify-content-center border-circle bg-black-alpha-60 text-white"
                                                        style={{
                                                            top: "50%",
                                                            left: "50%",
                                                            width: "2.5rem",
                                                            height: "2.5rem",
                                                            transform: "translate(-50%, -50%)",
                                                            boxShadow: "0 1px 4px rgba(0,0,0,0.4)",
                                                            pointerEvents: "none",
                                                        }}
                                                        aria-hidden="true"
                                                    >
                                                        <i className="pi pi-play" style={{ fontSize: "1rem", marginLeft: "2px" }} />
                                                    </span>
                                                </>
                                            ) : (
                                                <img
                                                    key={getMediaCacheToken(item, previewRevisionById[item._id]) || item._id}
                                                    src={getImageSrc(item, previewRevisionById[item._id])}
                                                    srcSet={buildSrcSet(item, previewRevisionById[item._id])}
                                                    sizes="(max-width: 576px) 50vw, (max-width: 768px) 33vw, (max-width: 992px) 25vw, 16vw"
                                                    alt="JobStock item"
                                                    style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                                                />
                                            )}
                                            <span
                                                className="absolute flex align-items-center justify-content-center gap-1 border-round bg-black-alpha-60 text-white"
                                                style={{
                                                    top: "0.4rem",
                                                    left: "0.4rem",
                                                    minWidth: "1.5rem",
                                                    height: "1.5rem",
                                                    padding: "0 0.4rem",
                                                    fontSize: "0.75rem",
                                                    fontWeight: 600,
                                                    lineHeight: 1,
                                                    boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
                                                }}
                                                title={`Used ${item.usageCount ?? 0}, replaced ${item.replacedCount ?? 0}`}
                                                aria-label={`Used ${item.usageCount ?? 0}, replaced ${item.replacedCount ?? 0}`}
                                            >
                                                <i className="pi pi-clone" style={{ fontSize: "0.7rem" }} />
                                                <span>{item.usageCount ?? 0} / {item.replacedCount ?? 0}</span>
                                            </span>
                                            {(!item.reviewed && itemHasVisibleText(item)) || item.archived || item.reviewed ? (
                                                <div
                                                    className="absolute flex flex-column align-items-end gap-1"
                                                    style={{ top: "0.4rem", right: "0.4rem" }}
                                                >
                                                    {!item.reviewed && itemHasVisibleText(item) ? (
                                                        <span
                                                            className="flex align-items-center justify-content-center border-circle bg-red-500 text-white"
                                                            style={{
                                                                width: "1.5rem",
                                                                height: "1.5rem",
                                                                boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
                                                            }}
                                                            title="Contains text"
                                                            aria-label="Contains text"
                                                        >
                                                            <i className="pi pi-exclamation-triangle" style={{ fontSize: "0.75rem" }} />
                                                        </span>
                                                    ) : null}
                                                    {item.archived ? (
                                                        <span
                                                            className="flex align-items-center justify-content-center border-circle bg-red-500 text-white"
                                                            style={{
                                                                width: "1.5rem",
                                                                height: "1.5rem",
                                                                boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
                                                            }}
                                                            title="Archived"
                                                            aria-label="Archived"
                                                        >
                                                            <i className="pi pi-thumbs-down" style={{ fontSize: "0.75rem" }} />
                                                        </span>
                                                    ) : item.reviewed ? (
                                                        <span
                                                            className="flex align-items-center justify-content-center border-circle bg-blue-500 text-white"
                                                            style={{
                                                                width: "1.5rem",
                                                                height: "1.5rem",
                                                                boxShadow: "0 1px 3px rgba(0,0,0,0.35)",
                                                            }}
                                                            title="Reviewed"
                                                            aria-label="Reviewed"
                                                        >
                                                            <i className="pi pi-thumbs-up" style={{ fontSize: "0.75rem" }} />
                                                        </span>
                                                    ) : null}
                                                </div>
                                            ) : null}
                                        </button>
                                        {isReanalyzing(item) || needsReanalyze(item) || (item.status && item.status !== "ready") ? (
                                            <div className="text-xs text-600 mt-1 white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                                {isReanalyzing(item)
                                                    ? "re-analyzing"
                                                    : needsReanalyze(item)
                                                        ? `needs re-analyze · v${getItemVersion(item)}`
                                                        : item.status}
                                            </div>
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                            <Paginator
                                first={lazyState.first}
                                rows={lazyState.rows}
                                totalRecords={totalRecords}
                                rowsPerPageOptions={[25, 50, 100]}
                                onPageChange={onPage}
                                template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                                currentPageReportTemplate="{first} to {last} of {totalRecords}"
                            />
                        </>
                    )}
                </div>
            </div>

            <Dialog
                header={lightboxHeader}
                visible={showLightbox}
                style={{ width: "95vw", height: "92vh" }}
                contentStyle={{ height: "calc(92vh - 5rem)", overflow: "hidden", padding: 0 }}
                modal
                maximizable={false}
                onHide={closeLightbox}
            >
                {selectedItem && (
                    <div className="flex h-full" style={{ minHeight: 0 }}>
                        <div className="flex-1 flex flex-column align-items-center justify-content-center p-3 overflow-auto" style={{ minWidth: 0 }}>
                            {selectedItem.processingError && (
                                <div className="text-red-500 w-full text-center mb-2">{selectedItem.processingError}</div>
                            )}
                            {getLatestVisionAnalysisError(selectedItem) && (
                                <div className="text-red-500 w-full text-center mb-2">
                                    {getLatestVisionAnalysisError(selectedItem)}
                                </div>
                            )}
                            {isBusyItem(selectedItem) ? (
                                <div aria-busy="true" style={{ width: "100%", maxWidth: selectedViewport.width, minHeight: "12rem" }}>
                                    <Skeleton width="100%" height="12rem" className="jobstock-processing-skeleton" />
                                </div>
                            ) : (
                                <div
                                    className="surface-100 border-1 surface-border border-round overflow-hidden flex align-items-center justify-content-center"
                                    style={{
                                        width: "100%",
                                        maxWidth: selectedViewport.width,
                                        height: "100%",
                                        minHeight: "16rem",
                                        transition: "max-width 0.2s ease",
                                    }}
                                >
                                    {isVideo(selectedItem) ? (
                                        <video
                                            key={getMediaCacheToken(selectedItem, previewRevisionById[selectedItem._id]) || selectedItem._id}
                                            src={getMediaSrc(selectedItem, previewRevisionById[selectedItem._id])}
                                            controls
                                            style={{ width: "100%", maxHeight: "100%", objectFit: "contain" }}
                                        />
                                    ) : (
                                        <img
                                            key={getMediaCacheToken(selectedItem, previewRevisionById[selectedItem._id]) || selectedItem._id}
                                            src={getImageSrc(selectedItem, previewRevisionById[selectedItem._id])}
                                            srcSet={buildSrcSet(selectedItem, previewRevisionById[selectedItem._id])}
                                            sizes={`${selectedViewport.width}px`}
                                            alt={selectedItem.vision?.title || "JobStock item"}
                                            style={{ width: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                        {lightboxSidePanel}
                    </div>
                )}
            </Dialog>

            <Dialog
                header="Confirm Delete"
                visible={showDeleteDialog}
                modal
                footer={
                    <div>
                        <Button
                            label="No"
                            icon="pi pi-times"
                            onClick={() => setShowDeleteDialog(false)}
                            className="p-button-text"
                        />
                        <Button
                            label="Yes"
                            icon="pi pi-check"
                            onClick={deleteItem}
                            autoFocus
                            loading={deleteLoading}
                        />
                    </div>
                }
                onHide={() => setShowDeleteDialog(false)}
            >
                <div className="flex align-items-center justify-content-center">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: "2rem" }} />
                    <span>Are you sure you want to delete this JobStock item? This removes it from S3 as well.</span>
                </div>
            </Dialog>

            <Dialog
                header="Generate JobStock image"
                visible={showGenerateDialog}
                modal
                style={{ width: "42rem", maxWidth: "95vw" }}
                footer={
                    <div>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            onClick={() => setShowGenerateDialog(false)}
                            className="p-button-text"
                            disabled={generateLoading}
                        />
                        <Button
                            label="Generate"
                            icon="pi pi-sparkles"
                            onClick={submitGenerateAi}
                            autoFocus
                            loading={generateLoading}
                            disabled={
                                generateLoading
                                || generateOfferLoading
                                || !(generatePrompt || "").trim()
                                || (generateKind === GENERATE_KIND_OFFER_ICON && !generateOffer?.uri)
                            }
                        />
                    </div>
                }
                onHide={() => {
                    if (!generateLoading) {
                        setShowGenerateDialog(false);
                    }
                }}
            >
                <div className="flex flex-column gap-3">
                    <div className="flex flex-column gap-2">
                        <label className="text-sm font-semibold">Type</label>
                        <SelectButton
                            value={generateKind}
                            options={GENERATE_KIND_OPTIONS}
                            onChange={(event) => {
                                if (event.value) {
                                    handleGenerateKindChange(event.value);
                                }
                            }}
                            disabled={generateLoading || generateOfferLoading}
                            className="jobstock-selectbutton"
                        />
                    </div>
                    <div className="flex flex-column gap-2">
                        <label className="text-sm font-semibold">Model</label>
                        <SelectButton
                            value={generateProvider}
                            options={GENERATE_AI_IMAGE_PROVIDER_OPTIONS}
                            onChange={(event) => {
                                if (event.value) {
                                    setGenerateProvider(event.value);
                                }
                            }}
                            disabled={generateLoading}
                            className="jobstock-selectbutton"
                        />
                    </div>
                    {generateKind === GENERATE_KIND_OFFER_ICON ? (
                        <div className="flex flex-column gap-2 surface-100 border-1 surface-border border-round p-3">
                            <div className="flex align-items-center justify-content-between gap-2">
                                <div className="text-sm font-semibold">Suggested offer</div>
                                <Button
                                    type="button"
                                    label="Different random service"
                                    icon="pi pi-refresh"
                                    className="p-button-text p-button-sm"
                                    onClick={loadRandomSuggestedOffer}
                                    loading={generateOfferLoading}
                                    disabled={generateLoading || generateOfferLoading}
                                />
                            </div>
                            {generateOffer ? (
                                <div className="text-sm text-900">
                                    <div className="font-medium">{generateOffer.name || generateOffer.uri}</div>
                                    {(generateOffer.category || generateOffer.subcategory) && (
                                        <div className="text-500 mt-1">
                                            {[generateOffer.category, generateOffer.subcategory]
                                                .filter(Boolean)
                                                .join(" / ")}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="text-sm text-500">
                                    {generateOfferLoading ? "Loading random offer…" : "No offer selected"}
                                </div>
                            )}
                        </div>
                    ) : null}
                    <div className="flex flex-column gap-2">
                        <label htmlFor="jobstock-generate-prompt" className="text-sm font-semibold">
                            AI prompt
                        </label>
                        <InputTextarea
                            id="jobstock-generate-prompt"
                            value={generatePrompt}
                            onChange={(event) => setGeneratePrompt(event.target.value)}
                            rows={8}
                            autoResize
                            className="w-full"
                            disabled={generateLoading || generateOfferLoading}
                        />
                        <div className="text-xs text-500">
                            {generateKind === GENERATE_KIND_HERO
                                ? (generateProvider === AI_IMAGE_PROVIDER_BLACK_FOREST_LABS
                                    ? "Hero images generate at 2560×640 (landscape)."
                                    : generateProvider === AI_IMAGE_PROVIDER_BOTH
                                        ? "Hero images: Recraft 1536×768, Flux 2560×640 (landscape)."
                                        : "Hero images generate at 1536×768 (landscape).")
                                : "Offer icons generate at 1024×1024 (square)."}
                        </div>
                    </div>
                </div>
            </Dialog>

            <Dialog
                header="Regenerate with AI"
                visible={showRegenerateDialog}
                modal
                style={{ width: "42rem", maxWidth: "95vw" }}
                footer={
                    <div>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            onClick={() => setShowRegenerateDialog(false)}
                            className="p-button-text"
                            disabled={regenerateLoading}
                        />
                        <Button
                            label="Regenerate"
                            icon="pi pi-sparkles"
                            onClick={regenerateAiItem}
                            autoFocus
                            loading={regenerateLoading}
                            disabled={regenerateLoading || !(regeneratePrompt || "").trim()}
                        />
                    </div>
                }
                onHide={() => {
                    if (!regenerateLoading) {
                        setShowRegenerateDialog(false);
                    }
                }}
            >
                <div className="flex flex-column gap-3">
                    <div className="flex align-items-start">
                        <i className="pi pi-exclamation-triangle mr-3 mt-1" style={{ fontSize: "2rem" }} />
                        <span>
                            This will generate a new AI image using the prompt below and replace the files for this
                            JobStock item. The JobStock id stays the same, so existing site attachments keep working.
                            Edits to the prompt are saved when you regenerate.
                        </span>
                    </div>
                    <div className="flex flex-column gap-2">
                        <label className="text-sm font-semibold">Model</label>
                        <SelectButton
                            value={regenerateProvider}
                            options={AI_IMAGE_PROVIDER_OPTIONS}
                            onChange={(event) => {
                                if (event.value) {
                                    setRegenerateProvider(event.value);
                                }
                            }}
                            disabled={regenerateLoading}
                            className="jobstock-selectbutton"
                        />
                    </div>
                    <div className="flex flex-column gap-2">
                        <label htmlFor="jobstock-regenerate-prompt" className="text-sm font-semibold">
                            AI prompt
                        </label>
                        <InputTextarea
                            id="jobstock-regenerate-prompt"
                            value={regeneratePrompt}
                            onChange={(event) => setRegeneratePrompt(event.target.value)}
                            rows={6}
                            autoResize
                            className="w-full"
                            disabled={regenerateLoading}
                        />
                    </div>
                </div>
            </Dialog>

            <Dialog
                header="Edit title and description"
                visible={showVisionTextDialog}
                modal
                style={{ width: "32rem", maxWidth: "95vw" }}
                footer={
                    <div>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            onClick={() => setShowVisionTextDialog(false)}
                            className="p-button-text"
                            disabled={visionTextSaving}
                        />
                        <Button
                            label="Save"
                            icon="pi pi-check"
                            onClick={saveVisionText}
                            autoFocus
                            loading={visionTextSaving}
                        />
                    </div>
                }
                onHide={() => {
                    if (!visionTextSaving) {
                        setShowVisionTextDialog(false);
                    }
                }}
            >
                <div className="flex flex-column gap-3">
                    <div className="flex flex-column gap-2">
                        <label htmlFor="jobstock-vision-title" className="text-sm font-semibold">
                            Title
                        </label>
                        <InputText
                            id="jobstock-vision-title"
                            value={visionTextTitle}
                            onChange={(event) => setVisionTextTitle(event.target.value)}
                            className="w-full"
                            disabled={visionTextSaving}
                        />
                    </div>
                    <div className="flex flex-column gap-2">
                        <label htmlFor="jobstock-vision-description" className="text-sm font-semibold">
                            Description
                        </label>
                        <InputTextarea
                            id="jobstock-vision-description"
                            value={visionTextDescription}
                            onChange={(event) => setVisionTextDescription(event.target.value)}
                            rows={5}
                            autoResize
                            className="w-full"
                            disabled={visionTextSaving}
                        />
                    </div>
                </div>
            </Dialog>

            <JobStockCropDialog
                imageUrl={cropSourceUrl}
                visible={showCropDialog}
                onHide={() => setShowCropDialog(false)}
                onCreated={() => {
                    refetch();
                }}
            />
        </div>
    );
};
export default JobStock;
